import { useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FolderPlus, Pencil } from "@fg/icons";
import { driveApi, type DriveItem } from "@/api/driveApi";
import {
    useAllFolders,
    useCopyItems,
    useCopyTemplate,
    useCreateFolder,
    useDeleteItems,
    useEmptyTrash,
    useMoveItems,
    useRenameItem,
    useRestoreItems,
    useToggleFavorite,
    useTrashItems,
    useUnzip,
    useZipItems
} from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { downloadItems } from "@/lib/download";
import { APP_URLS, baseNameLength, categoryOf, officeAppUrl, officeKindOf } from "@/lib/fileTypes";
import { plural } from "@/lib/format";
import { DriveActionsContext, type DriveActions } from "./driveActions";
import { useToast } from "./toast/ToastProvider";
import { useDialogs } from "./dialogs/DialogsProvider";
import { ShareDialog } from "./ShareDialog";
import { VersionHistoryPanel } from "./VersionHistoryPanel";
import { PreviewOverlay } from "./preview/PreviewOverlay";
import { MoveDialog } from "./dialogs/MoveDialog";
import { ProtectPdfDialog } from "./dialogs/ProtectPdfDialog";
import { FileRequestDialog } from "./dialogs/FileRequestDialog";
import { ShortcutsDialog } from "./dialogs/ShortcutsDialog";
import { TemplateGallery } from "./templates/TemplateGallery";

export { useDriveActions, type DriveActions } from "./driveActions";

/** Stable facade whose methods always call the latest implementation. */
function useStableCallbacks<T extends object>(impl: T): T {
    const ref = useRef(impl);
    ref.current = impl;
    return useMemo(() => {
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(impl)) {
            out[key] = (...args: unknown[]) =>
                (ref.current as unknown as Record<string, (...a: unknown[]) => unknown>)[key]!(...args);
        }
        return out as T;
        // Keys never change between renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

/**
 * Owns every Drive action plus the app-wide overlays they open (preview,
 * share, version history, protect PDF, move picker, shortcuts).
 */
export function DriveUiProvider({ children }: { children: ReactNode }) {
    const token = useAccessToken();
    const navigate = useNavigate();
    const toast = useToast();
    const { confirm, prompt } = useDialogs();
    const folders = useAllFolders();
    const qc = useQueryClient();
    // The share dialog manages people/links through its own queries; the
    // activity timeline/feed should show what it just did once it closes.
    const closeShare = () => {
        setShareItem(null);
        void qc.invalidateQueries({ queryKey: ["drive", "activity"] });
    };

    const renameM = useRenameItem();
    const starM = useToggleFavorite();
    const trashM = useTrashItems();
    const restoreM = useRestoreItems();
    const deleteM = useDeleteItems();
    const emptyM = useEmptyTrash();
    const moveM = useMoveItems();
    const copyM = useCopyItems();
    const zipM = useZipItems();
    const unzipM = useUnzip();
    const folderM = useCreateFolder();
    const templateM = useCopyTemplate();

    const [gallery, setGallery] = useState<{ parentId: string | null } | null>(null);
    const [preview, setPreview] = useState<{ list: DriveItem[]; index: number } | null>(null);
    const [shareItem, setShareItem] = useState<DriveItem | null>(null);
    const [historyItem, setHistoryItem] = useState<DriveItem | null>(null);
    const [protectItem, setProtectItem] = useState<DriveItem | null>(null);
    const [requestFolder, setRequestFolder] = useState<DriveItem | null>(null);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [movePick, setMovePick] = useState<{
        items: DriveItem[];
        mode: "move" | "copy";
        resolve: (t: string | null | undefined) => void;
    } | null>(null);
    const pickFolder = (items: DriveItem[], mode: "move" | "copy") =>
        new Promise<string | null | undefined>((resolve) => setMovePick({ items, mode, resolve }));

    /** Where "Open in …" goes for a file: Docs / Sheets / Slides, or the PDF app. */
    const appUrlFor = (item: DriveItem): string | null => {
        if (item.kind === "File" && categoryOf(item) === "pdf") {
            // The PDF app opens (and saves back) Drive files by id.
            const url = new URL(APP_URLS.pdf);
            url.searchParams.set("fileId", item.id);
            return url.toString();
        }
        const kind = officeKindOf(item);
        if (!kind) return null;
        const url = new URL(officeAppUrl(kind));
        url.searchParams.set("fileId", item.id);
        url.searchParams.set("fileName", item.name);
        url.searchParams.set("openFile", driveApi.downloadUrl(item.id));
        return url.toString();
    };

    const folderName = (id: string | null) =>
        id === null ? "My Drive" : folders.data?.find((f) => f.id === id)?.name ?? "the folder";
    const describe = (items: DriveItem[]) => (items.length === 1 ? `“${items[0]!.name}”` : plural(items.length, "item"));
    const locate = (item: Pick<DriveItem, "id" | "parentId">) =>
        navigate(`${item.parentId ? `/folder/${encodeURIComponent(item.parentId)}` : "/"}?select=${encodeURIComponent(item.id)}`);

    const impl: DriveActions = {
        open: (item, list) => {
            if (item.isTrashed) {
                void (async () => {
                    const ok = await confirm({
                        title: "This item is in the bin",
                        message: `Restore “${item.name}” to open it.`,
                        confirmLabel: "Restore"
                    });
                    if (ok) await impl.restore([item]);
                })();
                return;
            }
            if (item.kind === "Folder") {
                navigate(`/folder/${encodeURIComponent(item.id)}`);
                return;
            }
            if (officeKindOf(item)) {
                impl.openInApp(item);
                return;
            }
            impl.preview(item, list);
        },

        preview: (item, list) => {
            if (item.kind !== "File") return;
            const files = (list ?? [item]).filter((i) => i.kind === "File");
            const index = files.findIndex((i) => i.id === item.id);
            setPreview(index < 0 ? { list: [item], index: 0 } : { list: files, index });
        },

        openInApp: (item, target) => {
            const url = appUrlFor(item);
            if (!url) {
                target?.close();
                toast.error(`No Fowlguard app can open “${item.name}”.`);
                return;
            }
            if (target && !target.closed) {
                // A tab we opened during the click (see createFromTemplate):
                // cut the opener link, as "noopener" would have, then navigate it.
                target.opener = null;
                target.location.href = url;
                return;
            }
            window.open(url, "_blank", "noopener,noreferrer");
        },

        download: async (items) => {
            if (items.length === 0) return;
            try {
                if (items.length > 1 || items[0]!.kind === "Folder") {
                    toast.show({ message: `Preparing ${describe(items)} as a zip…` });
                }
                await downloadItems(items.map((i) => i.id), token);
            } catch (e) {
                toast.error(e);
            }
        },

        rename: async (item) => {
            const name = await prompt({
                title: "Rename",
                defaultValue: item.name,
                selectLength: item.kind === "File" ? baseNameLength(item.name) : undefined,
                confirmLabel: "Rename",
                icon: <Pencil className="h-5 w-5 text-primary" />
            });
            if (name && name !== item.name) await impl.renameTo(item, name);
        },

        renameTo: async (item, name) => {
            try {
                await renameM.mutateAsync({ id: item.id, name });
                return true;
            } catch (e) {
                toast.error(e);
                return false;
            }
        },

        copy: async (items) => {
            if (items.length === 0) return;
            try {
                const copies = (await copyM.mutateAsync({ ids: items.map((i) => i.id) })).filter(Boolean);
                const first = copies[0];
                if (copies.length === 1 && first) {
                    toast.success(`Created “${first.name}”`, { action: { label: "Show", onClick: () => locate(first) } });
                } else {
                    toast.success(`Created ${plural(copies.length, "copy", "copies")}`);
                }
            } catch (e) {
                toast.error(e);
            }
        },

        copyTo: async (items) => {
            if (items.length === 0) return;
            const target = await pickFolder(items, "copy");
            if (target === undefined) return;
            try {
                const copies = (await copyM.mutateAsync({ ids: items.map((i) => i.id), parentId: target })).filter(Boolean);
                const first = copies[0];
                toast.success(
                    `Copied ${describe(items)} to ${folderName(target)}`,
                    copies.length === 1 && first ? { action: { label: "Show", onClick: () => locate(first) } } : undefined
                );
            } catch (e) {
                toast.error(e);
            }
        },

        move: async (items) => {
            if (items.length === 0) return;
            const target = await pickFolder(items, "move");
            if (target === undefined) return;
            await impl.moveTo(items, target);
        },

        moveTo: async (items, targetId) => {
            const moving = items.filter((i) => i.parentId !== targetId && i.id !== targetId);
            if (moving.length === 0) return;
            const origins = moving.map((item) => ({ item, from: item.parentId }));
            try {
                await moveM.mutateAsync(moving.map((item) => ({ item, newParentId: targetId })));
                toast.show({
                    message: `Moved ${describe(moving)} to ${folderName(targetId)}`,
                    action: {
                        label: "Undo",
                        onClick: () => {
                            moveM
                                .mutateAsync(origins.map((o) => ({ item: { ...o.item, parentId: targetId }, newParentId: o.from })))
                                .then(() => toast.show({ message: "Move undone" }), (e: unknown) => toast.error(e));
                        }
                    }
                });
            } catch (e) {
                toast.error(e);
            }
        },

        toggleStar: (items) => {
            if (items.length === 0) return;
            const fav = !items.every((i) => i.isFavorite);
            starM.mutate(
                { items, fav },
                {
                    onError: (e) => toast.error(e),
                    onSuccess: () => {
                        if (items.length > 1) {
                            toast.show({ message: fav ? `Added ${plural(items.length, "item")} to Starred` : `Removed ${plural(items.length, "item")} from Starred` });
                        }
                    }
                }
            );
        },

        share: (item) => setShareItem(item),

        copyLink: async (item) => {
            try {
                await navigator.clipboard.writeText(driveApi.openLink(item.id));
                toast.success(`Link to “${item.name}” copied — it opens for people with access`);
            } catch {
                toast.error(new Error("Couldn't copy the link — the browser blocked clipboard access."));
            }
        },
        history: (item) => setHistoryItem(item),
        protect: (item) => setProtectItem(item),
        requestFiles: (folder) => {
            if (folder.kind === "Folder" && !folder.ownerEmail) setRequestFolder(folder);
        },

        unzip: async (item) => {
            const tid = toast.show({ message: `Extracting “${item.name}”…`, durationMs: 120_000 });
            try {
                const out = await unzipM.mutateAsync(item.id);
                toast.dismiss(tid);
                toast.success(`Extracted ${plural(out?.length ?? 0, "item")} from “${item.name}”`);
            } catch (e) {
                toast.dismiss(tid);
                toast.error(e);
            }
        },

        trash: async (items) => {
            if (items.length === 0) return;
            const ids = items.map((i) => i.id);
            try {
                await trashM.mutateAsync(ids);
                toast.show({
                    message: `${describe(items)} moved to bin`,
                    action: {
                        label: "Undo",
                        onClick: () => {
                            restoreM.mutateAsync(ids).then(() => toast.show({ message: "Restored" }), (e: unknown) => toast.error(e));
                        }
                    }
                });
            } catch (e) {
                toast.error(e);
            }
        },

        restore: async (items) => {
            if (items.length === 0) return;
            try {
                await restoreM.mutateAsync(items.map((i) => i.id));
                const first = items[0];
                toast.success(
                    `Restored ${describe(items)}`,
                    items.length === 1 && first ? { action: { label: "Show", onClick: () => locate(first) } } : undefined
                );
            } catch (e) {
                toast.error(e);
            }
        },

        deleteForever: async (items) => {
            if (items.length === 0) return;
            const ok = await confirm({
                title: items.length === 1 ? "Delete forever?" : `Delete ${items.length} items forever?`,
                message: `${describe(items)} will be deleted forever. You can't undo this.`,
                confirmLabel: "Delete forever",
                destructive: true
            });
            if (!ok) return;
            try {
                await deleteM.mutateAsync(items.map((i) => i.id));
                toast.show({ message: `Deleted ${describe(items)} forever` });
            } catch (e) {
                toast.error(e);
            }
        },

        emptyBin: async (count) => {
            const ok = await confirm({
                title: "Empty bin?",
                message: `${count === 1 ? "The item" : `All ${count} items`} in the bin will be deleted forever. You can't undo this.`,
                confirmLabel: "Empty bin",
                destructive: true
            });
            if (!ok) return;
            try {
                await emptyM.mutateAsync();
                toast.show({ message: "Bin emptied" });
            } catch (e) {
                toast.error(e);
            }
        },

        zip: async (items, parentId) => {
            if (items.length === 0) return;
            const suggested = items.length === 1 ? items[0]!.name.replace(/\.[^.]+$/, "") : "Archive";
            const name = await prompt({
                title: "Zip into Drive",
                description: `Creates a .zip of ${describe(items)} in ${folderName(parentId)}.`,
                label: "Archive name",
                defaultValue: suggested,
                confirmLabel: "Create zip"
            });
            if (!name) return;
            const fileName = /\.zip$/i.test(name) ? name : `${name}.zip`;
            const tid = toast.show({ message: `Zipping ${describe(items)}…`, durationMs: 120_000 });
            try {
                const z = await zipM.mutateAsync({ ids: items.map((i) => i.id), name: fileName, parentId });
                toast.dismiss(tid);
                toast.success(`Created “${z.name}”`, { action: { label: "Show", onClick: () => locate(z) } });
            } catch (e) {
                toast.dismiss(tid);
                toast.error(e);
            }
        },

        newFolder: async (parentId) => {
            const name = await prompt({
                title: "New folder",
                defaultValue: "Untitled folder",
                confirmLabel: "Create",
                icon: <FolderPlus className="h-5 w-5 text-amber-500" />
            });
            if (!name) return;
            try {
                const f = await folderM.mutateAsync({ parentId, name });
                toast.success(`Created “${f.name}” in ${folderName(parentId)}`, {
                    action: { label: "Open", onClick: () => navigate(`/folder/${encodeURIComponent(f.id)}`) }
                });
            } catch (e) {
                toast.error(e);
            }
        },

        openTemplates: (parentId) => setGallery({ parentId }),

        createFromTemplate: async (templateId, parentId) => {
            // Open the editor's tab now, while this still runs inside the click —
            // a window.open after the await would be blocked as a pop-up.
            const tab = window.open("", "_blank");
            if (tab) {
                try {
                    tab.document.title = "Creating…";
                    tab.document.body.style.font = "14px system-ui, sans-serif";
                    tab.document.body.style.padding = "24px";
                    tab.document.body.textContent = "Creating your file…";
                } catch {
                    /* the tab is already gone */
                }
            }
            try {
                const item = await templateM.mutateAsync({ id: templateId, parentId });
                toast.success(
                    `“${item.name}” created in ${folderName(parentId)}`,
                    tab
                        ? { action: { label: "Show", onClick: () => locate(item) } }
                        : { action: { label: "Open", onClick: () => impl.openInApp(item) } }
                );
                impl.openInApp(item, tab);
                return item;
            } catch (e) {
                tab?.close();
                toast.error(e);
                return null;
            }
        },

        showShortcuts: () => setShortcutsOpen(true)
    };

    const actions = useStableCallbacks(impl);

    return (
        <DriveActionsContext.Provider value={actions}>
            {children}
            {preview ? (
                <PreviewOverlay
                    list={preview.list}
                    index={preview.index}
                    onIndexChange={(index) => setPreview((p) => (p ? { ...p, index } : p))}
                    onClose={() => setPreview(null)}
                />
            ) : null}
            {shareItem ? (
                // ShareDialog is owned by the sharing redesign — wrap rather than edit
                // so Esc closes it and page shortcuts pause while it is open.
                <div
                    data-drive-overlay=""
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Escape") closeShare();
                    }}
                >
                    <ShareDialog item={shareItem} onClose={closeShare} />
                </div>
            ) : null}
            {historyItem ? <VersionHistoryPanel item={historyItem} onClose={() => setHistoryItem(null)} /> : null}
            {protectItem ? <ProtectPdfDialog item={protectItem} onClose={() => setProtectItem(null)} /> : null}
            {requestFolder ? <FileRequestDialog folder={requestFolder} onClose={() => setRequestFolder(null)} /> : null}
            {gallery ? (
                <TemplateGallery
                    folderName={folderName(gallery.parentId)}
                    onClose={() => setGallery(null)}
                    onPick={(t) => impl.createFromTemplate(t.id, gallery.parentId)}
                />
            ) : null}
            {movePick ? (
                <MoveDialog
                    items={movePick.items}
                    mode={movePick.mode}
                    onClose={() => {
                        movePick.resolve(undefined);
                        setMovePick(null);
                    }}
                    onPick={(target) => {
                        movePick.resolve(target);
                        setMovePick(null);
                    }}
                />
            ) : null}
            <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        </DriveActionsContext.Provider>
    );
}
